import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Clock,
  Calendar,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle2,
  AlertCircle,
  Users,
  Target,
  Loader2,
  MessageSquare,
  FileText,
  Award,
  AlertTriangle,
  Quote,
  ChevronRight,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

import {
  getCall,
  formatDuration,
  getSentimentColor,
  type CallWithDetails,
  type MEDDICScores,
} from '@/api/insights';

export function CallDetailPage() {
  const { projectId, callId } = useParams<{ projectId: string; callId: string }>();
  const navigate = useNavigate();

  const { data: call, isLoading, error } = useQuery({
    queryKey: ['call', callId],
    queryFn: () => getCall(callId!, true) as Promise<CallWithDetails>,
    enabled: !!callId,
  });

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !call) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <p className="text-gray-500">Call not found or failed to load</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate(`/project/${projectId}/insights`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Insights
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const getSentimentIcon = (sentiment: string | null) => {
    switch (sentiment) {
      case 'positive':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'negative':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-gray-400" />;
    }
  };

  const getMEDDICLabel = (key: string): string => {
    const labels: Record<string, string> = {
      metrics: 'Metrics',
      economic_buyer: 'Economic Buyer',
      decision_criteria: 'Decision Criteria',
      decision_process: 'Decision Process',
      identify_pain: 'Identify Pain',
      champion: 'Champion',
    };
    return labels[key] || key;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/project/${projectId}/insights`)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {call.title || `Call ${call.id.slice(0, 8)}`}
              </h1>
              <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                <span className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {new Date(call.call_date).toLocaleDateString()}
                </span>
                {call.duration_seconds && (
                  <span className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {formatDuration(call.duration_seconds)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {call.analysis && (
            <div className="flex items-center space-x-3">
              <Badge className={cn(
                'text-sm',
                getSentimentColor(call.analysis.sentiment)
              )}>
                {getSentimentIcon(call.analysis.sentiment)}
                <span className="ml-1 capitalize">{call.analysis.sentiment || 'Unknown'}</span>
              </Badge>

              {call.scorecards?.[0] && (
                <Badge variant="outline" className="text-sm">
                  <Award className="h-4 w-4 mr-1" />
                  Score: {call.scorecards[0].overall_score?.toFixed(1) || '--'}/5
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Transcript & Audio */}
          <div className="lg:col-span-2 space-y-6">
            {/* Summary Card */}
            {call.analysis?.summary && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-blue-500" />
                    Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 dark:text-gray-300">{call.analysis.summary}</p>
                </CardContent>
              </Card>
            )}

            {/* Transcript */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2 text-purple-500" />
                  Transcript
                </CardTitle>
                <CardDescription>
                  {call.transcript?.length || 0} segments
                </CardDescription>
              </CardHeader>
              <CardContent>
                {call.transcript?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No transcript available</p>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                    {call.transcript?.map((segment, index) => (
                      <div key={segment.id} className="flex space-x-3">
                        <div className="flex-shrink-0 w-20 text-right">
                          <span className="text-xs text-gray-400">
                            {segment.start_time_ms
                              ? formatDuration(Math.floor(segment.start_time_ms / 1000))
                              : `#${index + 1}`}
                          </span>
                        </div>
                        <div className="flex-1">
                          {segment.speaker_name && (
                            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                              {segment.speaker_name}:
                            </span>
                          )}
                          <p className="text-gray-700 dark:text-gray-300">{segment.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Insights */}
          <div className="space-y-6">
            {/* MEDDIC Scorecard */}
            {call.scorecards?.[0] && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Award className="h-5 w-5 mr-2 text-yellow-500" />
                    MEDDIC Scorecard
                  </CardTitle>
                  <CardDescription>
                    AI-generated coaching scores
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(call.scorecards[0].scores as MEDDICScores).map(([key, score]) => (
                      <div key={key}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600 dark:text-gray-400">
                            {getMEDDICLabel(key)}
                          </span>
                          <span className="font-medium">{score}/5</span>
                        </div>
                        <Progress
                          value={(score / 5) * 100}
                          className="h-2"
                        />
                      </div>
                    ))}

                    {call.scorecards[0].feedback && (
                      <>
                        <Separator className="my-4" />
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Coaching Feedback
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {call.scorecards[0].feedback}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Items */}
            {call.analysis?.action_items && call.analysis.action_items.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
                    Action Items
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {call.analysis.action_items.map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <div className="mt-0.5">
                          <div className={cn(
                            'h-2 w-2 rounded-full',
                            item.priority === 'high' && 'bg-red-500',
                            item.priority === 'medium' && 'bg-yellow-500',
                            item.priority === 'low' && 'bg-gray-400'
                          )} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {item.description}
                          </p>
                          {item.assignee && (
                            <p className="text-xs text-gray-500 mt-1">
                              Assigned to: {item.assignee}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Topics */}
            {call.analysis?.topics && call.analysis.topics.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Target className="h-5 w-5 mr-2 text-blue-500" />
                    Topics Discussed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {call.analysis.topics.map((topic, index) => (
                      <Badge key={index} variant="secondary">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Objections */}
            {call.analysis?.objections && call.analysis.objections.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2 text-orange-500" />
                    Objections Raised
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {call.analysis.objections.map((objection, index) => (
                      <li
                        key={index}
                        className="text-sm text-gray-700 dark:text-gray-300 flex items-start space-x-2"
                      >
                        <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <span>{objection}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Key Quotes */}
            {call.analysis?.key_quotes && call.analysis.key_quotes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Quote className="h-5 w-5 mr-2 text-indigo-500" />
                    Key Quotes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {call.analysis.key_quotes.map((quote, index) => (
                      <div key={index} className="border-l-2 border-indigo-300 pl-3">
                        <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                          "{quote.text}"
                        </p>
                        {quote.speaker && (
                          <p className="text-xs text-gray-500 mt-1">
                            — {quote.speaker}
                          </p>
                        )}
                        {quote.context && (
                          <p className="text-xs text-gray-400 mt-1">
                            {quote.context}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Competitors */}
            {call.analysis?.competitors_mentioned && call.analysis.competitors_mentioned.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Users className="h-5 w-5 mr-2 text-red-500" />
                    Competitors Mentioned
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {call.analysis.competitors_mentioned.map((competitor, index) => (
                      <Badge key={index} variant="destructive">
                        {competitor}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Next Steps */}
            {call.analysis?.next_steps && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Next Steps</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {call.analysis.next_steps}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
